import React, { ForwardedRef, useCallback, useImperativeHandle, useRef } from 'react';
import cn from 'classnames';

import { IChangeableProps } from '../types/changeable';
import { IFocusableAttrs } from '../types/focusable';

import s from './index.module.css';

interface ISimpleListItem {
  element?: React.ReactChild;
  title: string;
  value: string;
}

interface ISubListItem {
  title: string;
  items: ISimpleListItem[];
}

type TListItems = ISimpleListItem[] | ISubListItem[];

const isSubList = (item: ISubListItem | ISimpleListItem): item is ISubListItem => 'items' in item;

interface IListInputProps<TName extends string = string>
  extends IChangeableProps<string | undefined, TName> {
  items: TListItems;
  maxVisibleItems?: number;
  className?: string;
}

interface IListInputAttrs extends IFocusableAttrs {
  scrollIntoView(): void;
}

const getCurrentItem = (
  rootNodeRef: React.RefObject<HTMLDivElement>,
  value: string | undefined,
): HTMLButtonElement | null => {
  const button = rootNodeRef.current?.querySelector<HTMLButtonElement>(
    `.${s.itemButton}[name="${value}"]`,
  );

  return (
    button ?? rootNodeRef.current?.querySelector<HTMLButtonElement>(`.${s.itemButton}`) ?? null
  );
};

const renderListItem = (
  item: ISimpleListItem,
  handleChange: (event: React.MouseEvent<HTMLButtonElement>) => void,
) => (
  <li className={s.item} key={item.value}>
    <button
      className={s.itemButton}
      name={item.value}
      type="button"
      onClick={handleChange}
      data-test-id="list-input__button"
    >
      {item.element ?? item.title}
    </button>
  </li>
);

const renderSubList = (
  { items, title }: ISubListItem,
  handleChange: (event: React.MouseEvent<HTMLButtonElement>) => void,
) => (
  <li className={s.subListWrapper} key={`#sublist-${title}`} data-test-id="list-input__sub-group">
    <div className={s.subTitle} data-test-id="list-input__sub-group-title">
      {title}
    </div>
    <ul className={s.subList}>{items.map(subItem => renderListItem(subItem, handleChange))}</ul>
  </li>
);

const ListInput = React.forwardRef((props: IListInputProps, ref: ForwardedRef<IListInputAttrs>) => {
  const { items, name, value: inputValue, onChange, className, maxVisibleItems = 10 } = props;

  const rootNode = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (event: React.SyntheticEvent<HTMLButtonElement>) => {
      const value = event.currentTarget.name;

      onChange(event, { name, value });
    },
    [onChange, name],
  );

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Tab') {
      const buttons: HTMLButtonElement[] = [
        ...(rootNode.current?.querySelectorAll<HTMLButtonElement>(`.${s.itemButton}`) ?? []),
      ];
      const current = rootNode.current?.querySelector<HTMLButtonElement>(`.${s.itemButton}:focus`);
      const currentIdx = current ? buttons.indexOf(current) : 0;
      const offset = event.key === 'ArrowUp' ? -1 : 1;
      const nextIdx = (buttons.length + currentIdx + offset) % buttons.length;

      requestAnimationFrame(() => buttons[nextIdx]?.focus());
    }
  }, []);

  useImperativeHandle(
    ref,
    (): IListInputAttrs => ({
      blur: () => {
        rootNode.current?.blur();
      },
      focus: (options?: FocusOptions) => {
        getCurrentItem(rootNode, inputValue)?.focus(options);
      },
      scrollIntoView: () => {
        rootNode.current?.scrollIntoView();
      },
    }),
    [inputValue],
  );

  return (
    <div
      data-test-id="list-input"
      className={cn(s.root, className)}
      onKeyDown={handleKeyDown}
      ref={rootNode}
      style={{ '--max-visible-items': maxVisibleItems } as React.CSSProperties}
    >
      <ul className={s.list}>
        {items.map(item => {
          if (isSubList(item)) {
            return renderSubList(item, handleChange);
          } else {
            return renderListItem(item, handleChange);
          }
        })}
      </ul>
    </div>
  );
});

export type { IListInputProps, IListInputAttrs, ISimpleListItem, ISubListItem, TListItems };
export { ListInput, isSubList };
